package ai.wagemix.web.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import com.fsryan.foundation.web.theme.applyFSColorTokens
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.layer
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.layer.SilkLayer
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.MutablePalette
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette

/** The font stack of the redesign; Inter is self-hosted by the site under `/fonts/`. */
val WM_FONT_FAMILIES = listOf("Inter", "system-ui", "-apple-system", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", "sans-serif")

/**
 * Installs the Wagemix web theme over the foundation one. Call it from the site's single
 * `@InitSilk`, after `initFSWebTheme(ctx)`:
 *
 * ```
 * @InitSilk
 * fun initSite(ctx: InitSilkContext) {
 *     initFSWebTheme(ctx)
 *     initWMWebTheme(ctx)
 * }
 * ```
 *
 * It re-declares every foundation colour role with the brand ([WMColorTokens]), fills the
 * site-only [WMWebColors] group, and sets the body font. Breakpoints and the initial colour
 * mode stay as foundation set them (system preference, persisted under the foundation key).
 */
fun initWMWebTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.applyFSColorTokens(WMColorTokens.light)
    ctx.theme.palettes.dark.applyFSColorTokens(WMColorTokens.dark)

    ctx.theme.palettes.light.wagemix.apply {
        chartCanvas = Color.rgb(0xF8FAFB)
        chartSeries = Color.rgb(0x2C84F4)
        chartProjection = Color.rgb(0x00BFAE)
        heroGlow = Color.rgba(0x25, 0x63, 0xEB, 0.08f)
        shadow = Color.rgba(0x0F, 0x17, 0x2A, 0.08f)
        tierBronze = Color.rgb(0xB45309)
        tierSilver = Color.rgb(0x64748B)
        tierGold = Color.rgb(0xCA8A04)
        tierPlatinum = Color.rgb(0x0E7490)
        tierDiamond = Color.rgb(0x7C3AED)
    }
    ctx.theme.palettes.dark.wagemix.apply {
        chartCanvas = Color.rgb(0x181C20)
        chartSeries = Color.rgb(0x2C84F4)
        chartProjection = Color.rgb(0x00BFAE)
        heroGlow = Color.rgba(0x60, 0xA5, 0xFA, 0.12f)
        shadow = Color.rgba(0x00, 0x00, 0x00, 0.4f)
        tierBronze = Color.rgb(0xF59E0B)
        tierSilver = Color.rgb(0xCBD5E1)
        tierGold = Color.rgb(0xFACC15)
        tierPlatinum = Color.rgb(0x22D3EE)
        tierDiamond = Color.rgb(0xA78BFA)
    }

    ctx.stylesheet.layer(SilkLayer.BASE) {
        registerStyleBase("body") {
            Modifier.fontFamily(*WM_FONT_FAMILIES.toTypedArray())
        }
    }
}

/**
 * Read the theme from a composable: `WMWebTheme.colors.chartCanvas`. Styles read
 * `colorMode.toPalette().wagemix` instead, so one style serves both modes.
 */
object WMWebTheme {
    val colors: WMWebColors
        @Composable
        @ReadOnlyComposable
        get() = ColorMode.current.toPalette().wagemix
}

/** The site-only colours of [palette]. */
val MutablePalette.wagemixColors: WMWebColors
    get() = wagemix
